/**
 * Media URL resolution — images in the DB use standard markdown syntax:
 *   ![alt](/api/media/attachments/doc_node/cli-assets/foo.svg/uuid)
 *
 * No custom tag parsing needed. The FE just renders standard markdown.
 * This module provides helpers for constructing media URLs during upload.
 */

let _mediaBaseUrl: string | null = null;
let _fetchPromise: Promise<string> | null = null;

async function fetchMediaBaseUrl(): Promise<string> {
  try {
    const res = await fetch("/api/media/config");
    if (res.ok) {
      const data = await res.json();
      return data.mediaBaseUrl || "/api/media";
    }
  } catch {
    // fallback
  }
  return "/api/media";
}

/** Get the media base URL (cached after first call). */
export async function getMediaBaseUrl(): Promise<string> {
  if (_mediaBaseUrl) return _mediaBaseUrl;
  if (!_fetchPromise) {
    _fetchPromise = fetchMediaBaseUrl().then((url) => {
      _mediaBaseUrl = url;
      return url;
    });
  }
  return _fetchPromise;
}

/** Synchronous getter — returns cached value or fallback. */
export function getMediaBaseUrlSync(): string {
  return _mediaBaseUrl || "/api/media";
}

/** Resolve an s3 key to a full renderable URL. Each segment is
 * percent-encoded — a node path may hold spaces or unicode, and the URL goes
 * into Markdown (`![](…)`), where a raw space ends it. */
export function resolveMediaUrl(s3Key: string): string {
  const base = getMediaBaseUrlSync();
  return `${base}/${s3Key.split("/").map(encodeURIComponent).join("/")}`;
}
